import { ExternalLink, Home, MapPin, TrendingUp } from 'lucide-react';
import type { SecurityItem } from '@/types';

interface Props {
  securities: SecurityItem[];
}

/**
 * Market Research Panel — generates links to comparable property sales
 * on major AU property portals based on security item addresses.
 * Supports Domain, realestate.com.au, and CoreLogic.
 */
export function MarketResearchPanel({ securities }: Props) {
  const propertySecurities = securities.filter(s => s.securityType === 'REAL_PROPERTY');

  if (propertySecurities.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 py-8 text-center text-gray-400">
        <Home className="w-6 h-6 mx-auto mb-2" />
        <p className="text-sm">No real property securities to research</p>
        <p className="text-xs mt-1">Add a property security item to see comparable sales links</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-gray-400" />
        <h3 className="text-sm font-semibold text-gray-900">Market Research — Comparable Sales</h3>
      </div>

      {propertySecurities.map(security => (
        <PropertyResearchCard key={security.id} security={security} />
      ))}
    </div>
  );
}

function PropertyResearchCard({ security }: { security: SecurityItem }) {
  // Extract address parts from description or structured fields
  const address = security.description;
  const meta = security as any; // May have addressLine1, suburb, state, postcode from new fields

  // Build search queries for different portals
  const suburb = meta.suburb || extractSuburb(address);
  const state = meta.state || extractState(address);
  const postcode = meta.postcode || extractPostcode(address);

  const searchQuery = encodeURIComponent(address);
  const suburbQuery = encodeURIComponent(suburb || '');

  // Generate portal links
  const links = [
    {
      name: 'Domain — Sold',
      description: 'Recent sold properties nearby',
      url: suburb
        ? `https://www.domain.com.au/sold-listings/${slugify(suburb)}-${state?.toLowerCase()}-${postcode}/`
        : `https://www.domain.com.au/sold-listings/?suburb=${suburbQuery}`,
      color: 'bg-green-50 border-green-200 text-green-700',
      icon: '🏠',
    },
    {
      name: 'realestate.com.au — Sold',
      description: 'Sold properties in the area',
      url: suburb
        ? `https://www.realestate.com.au/sold/in-${slugify(suburb)},+${state?.toLowerCase()}+${postcode}/`
        : `https://www.realestate.com.au/sold/in-${searchQuery}/`,
      color: 'bg-red-50 border-red-200 text-red-700',
      icon: '🔑',
    },
    {
      name: 'Domain — For Sale',
      description: 'Current listings for comparison',
      url: suburb
        ? `https://www.domain.com.au/sale/${slugify(suburb)}-${state?.toLowerCase()}-${postcode}/`
        : `https://www.domain.com.au/sale/?suburb=${suburbQuery}`,
      color: 'bg-blue-50 border-blue-200 text-blue-700',
      icon: '📋',
    },
    {
      name: 'Google Maps — Nearby',
      description: 'View property location and surroundings',
      url:
        meta.latitude && meta.longitude
          ? `https://www.google.com/maps/@${meta.latitude},${meta.longitude},17z`
          : `https://www.google.com/maps/search/${searchQuery}`,
      color: 'bg-amber-50 border-amber-200 text-amber-700',
      icon: '📍',
    },
    {
      name: 'RP Data / CoreLogic',
      description: 'Property data and analytics',
      url: `https://www.corelogic.com.au/search?q=${searchQuery}`,
      color: 'bg-purple-50 border-purple-200 text-purple-700',
      icon: '📊',
    },
  ];

  const bestVal =
    security.valuerEstimate ?? security.internalEstimate ?? security.borrowerValuation;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-indigo-500" />
              <p className="text-sm font-medium text-gray-900">{address}</p>
            </div>
            {(suburb || state || postcode) && (
              <p className="text-xs text-gray-400 mt-0.5 ml-5.5">
                {[suburb, state, postcode].filter(Boolean).join(' ')}
              </p>
            )}
          </div>
          {bestVal && (
            <div className="text-right">
              <p className="text-xs text-gray-400">Best Estimate</p>
              <p className="text-sm font-semibold text-gray-900">
                {new Intl.NumberFormat('en-AU', {
                  style: 'currency',
                  currency: 'AUD',
                  minimumFractionDigits: 0,
                }).format(parseFloat(bestVal))}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Research links */}
      <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {links.map(link => (
          <a
            key={link.name}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-colors hover:shadow-sm ${link.color}`}
          >
            <span className="text-base">{link.icon}</span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium truncate">{link.name}</p>
              <p className="text-[10px] opacity-70 truncate">{link.description}</p>
            </div>
            <ExternalLink className="w-3 h-3 opacity-50 shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function extractSuburb(address: string): string {
  // Try to extract suburb from an address like "42 Smith St, Sydney NSW 2000"
  const parts = address.split(',');
  if (parts.length >= 2) {
    const location = parts[1].trim();
    // Remove state and postcode
    return location.replace(/\s+(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)\s+\d{4}$/i, '').trim();
  }
  return '';
}

function extractState(address: string): string {
  const match = address.match(/\b(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)\b/i);
  return match ? match[1].toUpperCase() : '';
}

function extractPostcode(address: string): string {
  const match = address.match(/\b(\d{4})\b/);
  return match ? match[1] : '';
}
